/**
 * react-query hook for the creator-wide Linked Wheel Map (SDD-24 / WL-052b,
 * surface 2). Mirrors useWheelGraph but keyed on a creator's user id and hitting
 * /wheel/creator/{userId}/graph/.
 *
 * The app is on react-query 3.39.3 (pre-`@tanstack` rename). Read-only surface:
 * no mutations, nothing to invalidate. The query only runs when the build flag is
 * ON and a userId is present, so a flag-off / half-flipped state fires no request
 * (byte-identical to today). getCreatorWheelGraph() 404s until the backend routes
 * deploy — the consuming component maps 404 to a gentle "unavailable" state.
 */
import { useQuery } from "react-query";

import { getCreatorWheelGraph } from "../../services/linkedWheels";
import { LINKED_WHEELS_ENABLED } from "./featureFlag";
import { parseGraph } from "./graphLayout";

/**
 * @param {number|string} userId  the creator whose wheels form the map
 * @param {{enabled?: boolean}} [options]
 */
export function useCreatorWheelGraph(userId, options = {}) {
  const enabled =
    LINKED_WHEELS_ENABLED && userId != null && options.enabled !== false;

  return useQuery(
    ["creatorWheelGraph", userId],
    async () => {
      const res = await getCreatorWheelGraph(userId);
      // No single-wheel fallback centre: the backend flags the hub node
      // (is_center) which parseGraph resolves.
      return parseGraph(res && res.data, null);
    },
    {
      enabled,
      staleTime: 60 * 1000,
      // Don't hammer a 404/403 (route not deployed yet / premium gate).
      retry: (failureCount, error) => {
        const s = error && error.response && error.response.status;
        if (s === 403 || s === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    }
  );
}

export default useCreatorWheelGraph;
